<script lang="ts">
  import CompareLab from '../components/meter/CompareLab.svelte';
  import LoopSlider from '../components/meter/LoopSlider.svelte';
  import type { OwnInitial } from '../components/meter/own-code';
  import OwnCodeMeter from '../components/meter/OwnCodeMeter.svelte';
  import PredictionPanel from '../components/meter/PredictionPanel.svelte';
  import Tabs from '../components/Tabs.svelte';
  import type { MeterDepth } from '../lib/router';
  import { catalog } from '../services';

  /**
   * 計算量メーター（layout-and-navigation.md §6）。上から「見積もり → N を変えて回数を見る → 実測」。
   * `?n` と `?depth` はスライダーと見積もりの初期値。`#/meter/:cardId` はカードの制約と ops を読む。
   */
  interface Props {
    cardId?: string | undefined;
    n?: number | undefined;
    depth?: MeterDepth | undefined;
  }

  let { cardId, n, depth }: Props = $props();

  const card = $derived(cardId !== undefined ? catalog.byId.get(cardId) : undefined);

  /** カードの制約 N の最大と ops 式（見積もり用）。 */
  const cardPredict = $derived.by((): { n?: number; ops?: string } => {
    if (!card) return {};
    if (card.type === 'complexity-quiz' || card.type === 'reduce') {
      const c = card.constraints?.find((x) => x.name === 'N');
      return { ...(c ? { n: c.max } : {}), ...(card.ops !== undefined ? { ops: card.ops } : {}) };
    }
    if (card.type === 'write-ac' && card.meter?.ops !== undefined) return { ops: card.meter.ops };
    return {};
  });

  const ownInitial = $derived.by((): OwnInitial | undefined => {
    if (card?.type !== 'write-ac' || !card.meter) return undefined;
    return {
      code: card.starter,
      input: card.meter.input,
      sizes: card.meter.sizes,
      ops: card.meter.ops,
    };
  });

  // 初期値だけを受け取り、以後は画面の操作が持つ。
  const init = (() => ({
    n: n ?? cardPredict.n ?? 200_000,
    sliderN: n ?? 20_000,
    depth: depth ?? 2,
    mode: ownInitial ? 'own' : 'compare',
  }))();
  let constraintN = $state(init.n);
  let sliderN = $state(Math.min(200_000, Math.max(10, init.sliderN)));
  let chosenDepth = $state<MeterDepth>(init.depth);
  let mode = $state<'compare' | 'own'>(init.mode === 'own' ? 'own' : 'compare');
</script>

<div class="page">
  <div class="page-head">
    <h1 tabindex="-1">計算量メーター</h1>
    {#if cardId}
      <span class="text-soft small"
        >カード <code>{cardId}</code> から開きました{#if !card}（見つかりませんでした）{/if}</span
      >
    {/if}
  </div>
  <p class="intro text-soft">
    書いたコードが時間制限に間に合うかを、実行する前に見積もり、実行して確かめます。
  </p>

  <div class="meter-grid">
    <section class="panel" aria-labelledby="p-title" data-testid="meter-predict">
      <h2 id="p-title">見積もり</h2>
      <p class="text-soft small">
        問題の制約（N
        の最大値）とループの重なり方から、いちばん内側のループが回る回数と、かかる秒数を、実行せずに見積もります。
      </p>
      <PredictionPanel bind:constraintN bind:depth={chosenDepth} ops={cardPredict.ops} />
    </section>

    <section class="panel" aria-labelledby="s-title" data-testid="meter-slider">
      <h2 id="s-title">N を変えて回数を見る</h2>
      <LoopSlider bind:n={sliderN} depth={chosenDepth} />
    </section>

    <section class="panel" aria-labelledby="m-title" data-testid="meter-measure">
      <h2 id="m-title">実測</h2>
      <p class="text-soft small">
        実際に実行して、N ごとに <code>viz.tick()</code> が呼ばれた回数（ステップ数）と実行時間を測ります。
      </p>
      <Tabs
        label="実測の種類"
        items={[
          { id: 'compare', label: '比較実験' },
          { id: 'own', label: '自分のコード' },
        ]}
        selected={mode}
        panelId="meter-measure-panel"
        idPrefix="meter-mode"
        onselect={(id) => (mode = id === 'own' ? 'own' : 'compare')}
      />
      <div id="meter-measure-panel" role="tabpanel" aria-labelledby="meter-mode-{mode}">
        {#if mode === 'compare'}
          <CompareLab />
        {:else}
          <OwnCodeMeter initial={ownInitial} />
        {/if}
      </div>
    </section>
  </div>
</div>

<style>
  .page-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-4);
    flex-wrap: wrap;
    margin-bottom: var(--space-5);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .intro {
    margin: calc(-1 * var(--space-3)) 0 var(--space-5);
  }
  .meter-grid {
    display: grid;
    gap: var(--space-5);
  }
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
</style>
